import React from 'react';

/**
 * Inline SVG sparkline — no charting dependency.
 *
 * Nulls are gaps, not zeros: a machine that went quiet must show a break in
 * the line rather than a plunge to the floor, so the series is drawn as one
 * path per run of consecutive readings.
 */

interface SparklineProps {
  values: (number | null)[];
  width?: number;
  height?: number;
  color?: string;
  /** Draw a soft fill under the line. */
  fill?: boolean;
  /** Force the y-axis to 0–100 (percentages) instead of fitting the data. */
  percent?: boolean;
  /** Render as bars — right for per-interval counter deltas. */
  bars?: boolean;
  className?: string;
}

export const Sparkline: React.FC<SparklineProps> = ({
  values,
  width = 120,
  height = 28,
  color = '#2dd4bf',
  fill = true,
  percent = false,
  bars = false,
  className,
}) => {
  // Stable per-instance id for the fill gradient. Declared before any early
  // return so the hook order never changes between renders.
  const gradientId = React.useMemo(
    () => `spark-${Math.random().toString(36).slice(2, 9)}`,
    [],
  );

  const numeric = values.filter((v): v is number => typeof v === 'number');
  if (numeric.length === 0) {
    return (
      <div
        className={className}
        style={{ width, height, display: 'flex', alignItems: 'center' }}
      >
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>no data</span>
      </div>
    );
  }

  const pad = 2;
  const min = percent ? 0 : Math.min(...numeric);
  const max = percent ? 100 : Math.max(...numeric);
  // A flat series would divide by zero; give it a centred line instead.
  const span = max - min || 1;

  const x = (i: number) =>
    values.length === 1 ? width / 2 : pad + (i / (values.length - 1)) * (width - pad * 2);
  const y = (v: number) => height - pad - ((v - min) / span) * (height - pad * 2);

  if (bars) {
    const barWidth = Math.max(1, (width - pad * 2) / values.length - 1);
    return (
      <svg width={width} height={height} className={className} aria-hidden="true">
        {values.map((v, i) =>
          typeof v === 'number' ? (
            <rect
              key={i}
              x={x(i) - barWidth / 2}
              y={y(v)}
              width={barWidth}
              height={Math.max(1, height - pad - y(v))}
              fill={color}
              opacity={0.75}
              rx={0.5}
            />
          ) : null,
        )}
      </svg>
    );
  }

  // Split into runs of consecutive non-null readings so gaps stay gaps.
  const runs: { i: number; v: number }[][] = [];
  let run: { i: number; v: number }[] = [];
  values.forEach((v, i) => {
    if (typeof v === 'number') {
      run.push({ i, v });
    } else if (run.length) {
      runs.push(run);
      run = [];
    }
  });
  if (run.length) runs.push(run);

  return (
    <svg width={width} height={height} className={className} aria-hidden="true">
      {fill && (
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
      )}

      {runs.map((points, ri) => {
        const line = points.map((p) => `${x(p.i)},${y(p.v)}`).join(' ');
        return (
          <g key={ri}>
            {fill && points.length > 1 && (
              <polygon
                points={`${x(points[0].i)},${height - pad} ${line} ${x(
                  points[points.length - 1].i,
                )},${height - pad}`}
                fill={`url(#${gradientId})`}
              />
            )}
            {points.length > 1 ? (
              <polyline
                points={line}
                fill="none"
                stroke={color}
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              <circle cx={x(points[0].i)} cy={y(points[0].v)} r={1.75} fill={color} />
            )}
          </g>
        );
      })}
    </svg>
  );
};

/**
 * Status timeline: one cell per reading, coloured by state. Gaps read as
 * "no contact", which is itself the signal worth seeing.
 */
export const StatusStrip: React.FC<{
  values: (string | null)[];
  width?: number;
  height?: number;
  className?: string;
}> = ({ values, width = 120, height = 12, className }) => {
  if (values.length === 0) return null;
  const cell = width / values.length;

  const colorFor = (v: string | null) => {
    if (v === null) return 'rgba(255,255,255,0.08)';       // no contact
    const s = v.toLowerCase();
    if (['online', 'active', 'healthy', 'running', 'ok'].includes(s)) return '#0d9488';
    if (['degraded', 'warning'].includes(s)) return '#d97706';
    return '#dc2626';
  };

  return (
    <svg width={width} height={height} className={className} aria-hidden="true">
      {values.map((v, i) => (
        <rect
          key={i}
          x={i * cell}
          y={0}
          width={Math.max(cell - 0.5, 0.5)}
          height={height}
          fill={colorFor(v)}
          rx={1}
        />
      ))}
    </svg>
  );
};
